import { useEffect, useState, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Loader2, Check, XCircle } from "lucide-react";

export default function PayPalReturn() {
  const { refresh } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [state, setState] = useState({ status: "capturing" });
  const capturingRef = useRef(false);

  useEffect(() => {
    if (capturingRef.current) return;
    capturingRef.current = true;
    const params = new URLSearchParams(location.search);
    const orderId = params.get("token") || params.get("order_id");
    if (!orderId) {
      setState({ status: "error", message: "Missing PayPal order token" });
      return;
    }
    (async () => {
      try {
        const { data } = await api.post("/paypal/capture", { order_id: orderId });
        if (data.status === "COMPLETED") {
          await refresh();
          setState({ status: "paid", amount: data.captured_amount });
        } else {
          setState({ status: "failed", message: `Status: ${data.status}` });
        }
      } catch (err) {
        setState({ status: "error", message: err?.response?.data?.detail || "Capture failed" });
      }
    })();
  }, [location.search, refresh]);

  return (
    <div className="max-w-[720px] mx-auto px-6 py-24" data-testid="paypal-return-page">
      {state.status === "capturing" && (
        <div className="text-center" data-testid="paypal-capturing">
          <Loader2 className="w-12 h-12 animate-spin mx-auto text-primary" />
          <h1 className="font-serif-display text-4xl mt-8 font-light">Capturing your PayPal payment…</h1>
          <p className="mt-4 text-muted-foreground">One moment while we finalize the order.</p>
        </div>
      )}
      {state.status === "paid" && (
        <div className="text-center" data-testid="paypal-paid">
          <div className="w-16 h-16 border-2 border-primary mx-auto flex items-center justify-center" style={{ borderRadius: 2 }}>
            <Check className="w-8 h-8 text-primary" />
          </div>
          <div className="font-mono-label text-primary mt-8">Porcelain Pro · Unlocked · via PayPal</div>
          <h1 className="font-serif-display text-5xl md:text-6xl mt-4 font-light leading-none tracking-tighter">Welcome to the craft.</h1>
          <p className="mt-6 text-muted-foreground">Lifetime access is active on your account.</p>
          <div className="mt-10 flex gap-3 justify-center">
            <button onClick={() => navigate("/courses")} className="btn-oxblood text-sm" data-testid="paypal-continue">Enter Pro curriculum</button>
            <button onClick={() => navigate("/dashboard")} className="btn-outline-mono text-sm">Go to dashboard</button>
          </div>
        </div>
      )}
      {(state.status === "failed" || state.status === "error") && (
        <div className="text-center" data-testid="paypal-failed">
          <XCircle className="w-12 h-12 mx-auto text-destructive" />
          <h1 className="font-serif-display text-4xl mt-6 font-light">PayPal capture failed.</h1>
          <p className="mt-4 text-muted-foreground">{state.message}</p>
          <button onClick={() => navigate("/pricing")} className="btn-oxblood text-sm mt-8">Try again</button>
        </div>
      )}
    </div>
  );
}
